/* eslint-disable @typescript-eslint/no-unused-expressions */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import axiosRetry from "axios-retry";
import { message } from "components/base";
import { AXIOS_TIMEOUT_ERROR_MESSAGE } from "constants/api";
import networkMonitor from "libs/network-monitor/NetworkMonitor";
import store from "redux/store";
import { setSyncProjectBimfile } from "utils/cache";
import { axiosController } from "./baseAxios";
import { axiosCacheController } from "./baseAxios";

interface AxiosClientProps {
  config: AxiosRequestConfig;
}

class AxiosClient {
  readonly instance: AxiosInstance;
  readonly config: AxiosRequestConfig;

  public constructor({ config }: AxiosClientProps) {
    this.config = config;
    const { headers, ...restConfig } = config;

    this.instance = axios.create({
      timeout: 600000,
      timeoutErrorMessage: AXIOS_TIMEOUT_ERROR_MESSAGE,
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
      ...restConfig,
    });

    this._initializeResponseInterceptor();
  }

  private _initializeResponseInterceptor = () => {
    this.instance.interceptors.request.use(
      async (config: AxiosRequestConfig) => {
        const isOnline = networkMonitor.isOnline();
        const bimFileId = store.getState().project?.dataProjectDetail?.id;
        // Assign project to synchronize from offline to online
        if (!isOnline && config.method !== "GET" && bimFileId) {
          setSyncProjectBimfile(bimFileId);
        }

        // not send any request if on page 403
        if (isOnline && window.location.pathname === "/access-error") {
          return;
        }
        const isShouldCache = !!config?.params?.shouldCache;

        if (isShouldCache) {
          config.signal = axiosCacheController.signal;
          //@ts-ignore
          config.throwErrorAndRetry = true;
        } else {
          config.signal = config.signal ?? axiosController.signal;
        }

        // Should cache is only used offline, but when online, if it is transmitted
        // the data in the cache will not match because the url is not the same
        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    this.instance.interceptors.response.use(
      async (response: AxiosResponse) => {
        const { data } = response;
        const errors = data?.errors || data?.data?.errors;
        const errorDetail = Array.isArray(errors) ? errors[0] : null;
        const errorCode = errorDetail?.code || 0;

        if (response?.status === 401) {
          return response.data;
        }

        if (
          //@ts-ignore
          response?.config?.throwErrorAndRetry &&
          (errorCode || (response?.status !== 200 && response?.status !== 202))
        ) {
          // if error -> we must retry
          throw Error("エラーが発生しました。再度実行お願いします。");
        }
        const isOnline = networkMonitor.isOnline();
        if (!isOnline && response?.status === 503) {
          return;
        }
        const syncOffline = errorDetail?.syncOffline || false;

        const isIgnoreShowMessageError =
          response?.config?.headers?.isIgnoreShowMessageError ||
          (syncOffline && errorCode);

        if (errors && isOnline && !isIgnoreShowMessageError) {
          message.error([
            "エラーが発生しました。再度実行お願いします。",
            "システムエラー",
          ]);
        }

        return response.data;
      },
      async (error: AxiosError) => {
        const data = error?.response?.data;
        const errors = data?.errors || data?.data?.errors;
        const errorDetail = Array.isArray(errors) ? errors?.[0] : null;
        const errorCode = errorDetail?.code || 0;
        const syncOffline = errorDetail?.syncOffline || false;

        const result = {
          apiPath: error.config.url,
          errorCode: error.response?.status || error.message,
          errors,
        };

        if (result.errorCode === 401) return Promise.resolve(result);

        // with case download project if has error -> alway throw error
        //@ts-ignore
        if (error?.config?.throwErrorAndRetry) {
          return Promise.reject(error);
        }
        const isOnline = networkMonitor.isOnline();
        if (axios.isCancel(error)) {
          return;
        }
        if (error?.config?.url?.includes("/forge-token")) {
          return;
        }

        if (!isOnline && error?.response?.status === 503) {
          return;
        }

        const { response, config } = error;
        const errStatus = response?.status!;
        if (
          errStatus === 403 &&
          config?.baseURL?.includes(process.env.REACT_APP_API_HOST_URL!)
        ) {
          return window.location.replace(
            `${window.location.origin}/access-error`
          );
        }

        const isIgnoreShowMessageError =
          error?.config?.headers?.isIgnoreShowMessageError ||
          (syncOffline && !!errorCode);

        if (
          isOnline &&
          !isIgnoreShowMessageError &&
          !error.config.url?.includes("/thumbnail")
        ) {
          if (errStatus === 500 && errorDetail?.shouldRetry) {
            return Promise.reject(error);
          }
          if (!isIgnoreShowMessageError) {
            message.error([
              "エラーが発生しました。再度実行お願いします。",
              "システムエラー",
            ]);
          }
        }

        return Promise.resolve(result);
      }
    );
  };
}

export const axiosClient = new AxiosClient({
  config: {
    baseURL: process.env.REACT_APP_API_HOST_URL || "",
    withCredentials: true,
  },
}).instance;

axiosRetry(axiosClient, {
  retries: 10,
  shouldResetTimeout: true,
  retryDelay: (retryCount) => {
    return retryCount * 3000;
  },
  async retryCondition(error: AxiosError) {
    const errors = error.response?.data.errors;
    const isOnline = networkMonitor.isOnline();
    const config = error.config;

    return (
      isOnline &&
      ((Array.isArray(errors) && errors[0]?.shouldRetry) ||
        //@ts-ignore
        config.throwErrorAndRetry)
    );
  },
});
